import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { logout } from '../../redux/authSlice';
import { AppDispatch, RootState } from '../../redux/store';
import { Menu, LogOut, Bell, ShieldCheck, User } from 'lucide-react';

interface AdminLayoutProps {
  children?: React.ReactNode;
  sidebar?: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, sidebar }) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
  };

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const sidebarElement = React.isValidElement(sidebar)
    ? React.cloneElement(sidebar as React.ReactElement<any>, {
        isOpen: isSidebarOpen,
        onNavigate: () => setIsSidebarOpen(false),
      })
    : sidebar;

  // Get page title from pathname
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.endsWith('/dashboard')) return 'Admin Dashboard';
    if (path.includes('/pending-registrations')) return 'Pending Registrations';
    if (path.includes('/document-approvals')) return 'Document Approvals';
    if (path.includes('/payment-requests')) return 'Payment Requests';
    if (path.includes('/admin-payments')) return 'All Payments';
    if (path.includes('/admin-action')) return 'Admin Actions';
    if (path.includes('/admin-review')) return 'Payment Review';
    if (path.includes('/profile')) return 'Admin Profile';
    if (path.includes('/user-management')) return 'User Management';
    if (path.includes('/bank-account')) return 'Bank Accounts';
    if (path.includes('/simulations')) return 'Simulations';
    if (path.includes('/virtual-accounts')) return 'Virtual Accounts';
    if (path.includes('/delete-unverified-users')) return 'Cleanup Users';
    return 'Admin Console';
  };

  const getPageSubtitle = () => {
    const path = location.pathname;
    if (path.endsWith('/dashboard')) return 'System overview and key metrics';
    if (path.includes('/pending-registrations')) return 'Review and approve new user registrations';
    if (path.includes('/document-approvals')) return 'Verify and approve KYC documents';
    if (path.includes('/payment-requests')) return 'Review pending payment requests';
    if (path.includes('/admin-payments')) return 'View all payment transactions';
    if (path.includes('/profile')) return 'Manage admin account security and 2FA';
    if (path.includes('/user-management')) return 'Manage user accounts and permissions';
    return '';
  };

  return (
    <div className="enterprise-dashboard-shell enterprise-dashboard-shell--admin flex h-screen overflow-hidden">
      {/* Sidebar */}
      {sidebarElement}

      {/* Main Content Area */}
      <div className="enterprise-dashboard-main flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <header className="enterprise-topbar flex-shrink-0 z-10">
          <div className="flex items-center justify-between px-4 sm:px-6 h-14">
            {/* Left: Mobile menu + Page title */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="lg:hidden p-2 rounded-lg text-fintech-textMuted hover:bg-slate-100 transition-colors"
                onClick={() => setIsSidebarOpen((prev) => !prev)}
                aria-label="Toggle sidebar menu"
              >
                <Menu size={20} />
              </button>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={14} className="text-fintech-secondary hidden sm:block" />
                  <h1 className="text-sm sm:text-base font-semibold text-fintech-text leading-tight truncate max-w-[140px] xs:max-w-[180px] sm:max-w-none">
                    {getPageTitle()}
                  </h1>
                </div>
                {getPageSubtitle() && (
                  <p className="text-xs text-fintech-textMuted hidden sm:block leading-tight mt-0.5">
                    {getPageSubtitle()}
                  </p>
                )}
              </div>
            </div>

            {/* Right: Admin badge + User actions */}
            <div className="flex items-center gap-2">
              {/* Admin badge */}
              <span className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-700">
                <ShieldCheck size={11} />
                Admin
              </span>

              {/* Date display */}
              <span className="hidden lg:block text-xs text-fintech-textMuted mr-1">
                {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>

              {/* Notification bell */}
              <button className="p-2 rounded-lg text-fintech-textMuted hover:bg-slate-100 transition-colors" aria-label="Notifications">
                <Bell size={18} />
              </button>

              {/* User menu */}
              <div className="flex items-center gap-2 pl-2 border-l border-fintech-border">
                <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                  <User size={14} className="text-white" />
                </div>
                <div className="hidden xs:block sm:block">
                  <p className="text-sm font-semibold text-fintech-text leading-tight truncate max-w-[80px] sm:max-w-none">{user?.name || 'Admin'}</p>
                  <p className="text-xs text-fintech-textMuted leading-tight">Administrator</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="ml-1 p-1.5 rounded-lg text-fintech-textMuted hover:text-fintech-error hover:bg-red-50 transition-colors"
                  title="Log out"
                  aria-label="Log out"
                >
                  <LogOut size={16} />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="enterprise-content p-4 sm:p-6">
            {children || <Outlet />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
